import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Tag, Search, X, Download, Loader2, Plus, GripVertical } from 'lucide-react'

// Shelf Price Tags — type to find a product, click to add it to the batch,
// then edit any field (name, size, code, price) before printing. The edits
// are PRINT-ONLY: shortening a long name or clearing the size for a cigar
// never touches the product record. Download renders the branded 88x55mm
// tags 10-up on A4. Backend: POST /admin/price-tags/pdf { tags: [...] }.

interface ProductHit {
  id: string
  name: string
  sku: string | null
  price_fils: number
  size_ml: number | null
  category_name: string | null
}
interface TagRow {
  key: string
  name: string
  size: string
  code: string
  priceJd: string
}

function toTag(p: ProductHit): TagRow {
  return {
    key: crypto.randomUUID(),
    name: p.name,
    size: p.size_ml && p.size_ml > 0 ? `${p.size_ml} ML` : '',
    code: p.sku ?? '',
    priceJd: (p.price_fils / 1000).toFixed(3),
  }
}

export default function PriceTags() {
  const [searchInput, setSearchInput] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [tags, setTags] = useState<TagRow[]>([])
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const boxRef = useRef<HTMLDivElement>(null)

  // Debounce the search input before hitting the API.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchInput.trim()), 220)
    return () => clearTimeout(t)
  }, [searchInput])

  // Close the dropdown on outside click.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const { data, isFetching } = useQuery<{ products: ProductHit[] }>({
    queryKey: ['pt-search', debounced],
    queryFn: () => api.get(`/products?search=${encodeURIComponent(debounced)}&active=true`).then(r => r.data),
    enabled: debounced.length >= 2,
  })
  const hits = (data?.products ?? []).slice(0, 8)

  function addTag(p: ProductHit) {
    setTags(prev => [...prev, toTag(p)])
    setSearchInput('')
    setDebounced('')
    setOpen(false)
  }
  function patch(key: string, field: keyof TagRow, value: string) {
    setTags(prev => prev.map(t => (t.key === key ? { ...t, [field]: value } : t)))
  }
  function remove(key: string) {
    setTags(prev => prev.filter(t => t.key !== key))
  }

  async function download() {
    const payload = tags
      .map(t => ({
        name: t.name.trim(),
        size: t.size.trim(),
        code: t.code.trim(),
        price_fils: Math.round((parseFloat(t.priceJd) || 0) * 1000),
      }))
      .filter(t => t.name && t.price_fils > 0)
    if (payload.length === 0) {
      setError('Add at least one tag with a name and a price.')
      return
    }
    setDownloading(true)
    setError(null)
    try {
      const res = await api.post('/price-tags/pdf', { tags: payload }, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'drinx-price-tags.pdf'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not generate the PDF')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Tag size={24} className="text-[#C41E3A]" />
          <div>
            <h1 className="text-2xl font-bold text-white">Price Tags</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Search a product, add it, edit the tag text if needed, then download. Edits are print-only.
            </p>
          </div>
        </div>
        <button
          onClick={download}
          disabled={tags.length === 0 || downloading}
          className="inline-flex items-center gap-2 rounded-lg bg-[#C41E3A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#a51930] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          Download PDF{tags.length > 0 ? ` (${tags.length})` : ''}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>
      )}

      {/* Autocomplete */}
      <div ref={boxRef} className="relative mb-5 max-w-xl">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        <input
          type="text"
          placeholder="Search a product by name or SKU..."
          value={searchInput}
          onChange={e => { setSearchInput(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          className="w-full pl-9 pr-9 py-2.5 text-sm rounded-lg border border-[#2e2e2e] bg-[#1a1a1a] text-white placeholder:text-gray-600 focus:border-[#C41E3A]/60 focus:outline-none"
        />
        {searchInput && (
          <button onClick={() => { setSearchInput(''); setDebounced('') }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"><X size={14} /></button>
        )}
        {open && debounced.length >= 2 && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-[#2e2e2e] bg-[#141414] shadow-2xl">
            {isFetching && hits.length === 0 && (
              <div className="px-3 py-3 text-xs text-gray-500">Searching…</div>
            )}
            {!isFetching && hits.length === 0 && (
              <div className="px-3 py-3 text-xs text-gray-500">No products match.</div>
            )}
            {hits.map(p => (
              <button
                key={p.id}
                onClick={() => addTag(p)}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-white/[0.04]"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm text-white">{p.name}</div>
                  <div className="text-[11px] text-gray-500 font-mono">
                    {p.sku ?? '—'}{p.size_ml && p.size_ml > 0 ? ` · ${p.size_ml} ML` : ''}{p.category_name ? ` · ${p.category_name}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-mono text-gray-400">JD {(p.price_fils / 1000).toFixed(3)}</span>
                  <Plus size={14} className="text-[#C41E3A]" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Editable tag list */}
      {tags.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#2e2e2e] bg-[#1a1a1a] px-4 py-12 text-center text-sm text-gray-500">
          No tags yet. Search above and click a product to add it.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#2e2e2e] bg-[#1a1a1a] divide-y divide-[#2e2e2e]">
          {tags.map((t, i) => (
            <div key={t.key} className="flex items-start gap-4 px-4 py-4">
              <span className="pt-2 text-gray-600" title={`Tag ${i + 1}`}><GripVertical size={14} /></span>
              {/* Fields: heading + subheading full width, then code + price */}
              <div className="flex-1 min-w-0 space-y-2">
                <Labeled label="Heading">
                  <input
                    value={t.name}
                    onChange={e => patch(t.key, 'name', e.target.value)}
                    className="w-full rounded-md border border-[#2e2e2e] bg-[#141414] px-2.5 py-1.5 text-sm text-white focus:border-[#C41E3A]/60 focus:outline-none"
                  />
                </Labeled>
                <Labeled label="Subheading">
                  <input
                    value={t.size}
                    onChange={e => patch(t.key, 'size', e.target.value)}
                    placeholder="(blank = no second line)"
                    className="w-full rounded-md border border-[#2e2e2e] bg-[#141414] px-2.5 py-1.5 text-sm text-gray-200 placeholder:text-gray-600 focus:border-[#C41E3A]/60 focus:outline-none"
                  />
                </Labeled>
                <div className="flex gap-2">
                  <Labeled label="Code" className="flex-1">
                    <input
                      value={t.code}
                      onChange={e => patch(t.key, 'code', e.target.value)}
                      placeholder="(blank = no code)"
                      className="w-full rounded-md border border-[#2e2e2e] bg-[#141414] px-2.5 py-1.5 text-sm font-mono text-gray-200 placeholder:text-gray-600 focus:border-[#C41E3A]/60 focus:outline-none"
                    />
                  </Labeled>
                  <Labeled label="Price (JD)" className="w-32">
                    <input
                      value={t.priceJd}
                      onChange={e => patch(t.key, 'priceJd', e.target.value)}
                      inputMode="decimal"
                      className="w-full rounded-md border border-[#2e2e2e] bg-[#141414] px-2.5 py-1.5 text-sm font-mono text-white focus:border-[#C41E3A]/60 focus:outline-none"
                    />
                  </Labeled>
                </div>
              </div>
              {/* Live preview */}
              <div className="shrink-0">
                <div className="mb-1 text-[10px] uppercase tracking-wider text-gray-500">Preview</div>
                <PreviewTag heading={t.name} subheading={t.size} code={t.code} priceJd={t.priceJd} />
              </div>
              <button onClick={() => remove(t.key)} className="mt-6 text-gray-500 hover:text-red-400" title="Remove"><X size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {tags.length > 0 && (
        <p className="mt-3 text-xs text-gray-600">
          {tags.length} tag{tags.length === 1 ? '' : 's'} · 10 per A4 page · leave Subheading or Code blank to omit it. The preview approximates the printed tag.
        </p>
      )}
    </div>
  )
}

function Labeled({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="mb-0.5 block text-[10px] uppercase tracking-wider text-gray-500">{label}</span>
      {children}
    </label>
  )
}

// Shrink-to-fit the heading: largest font (<= base px) whose width fits
// maxW, down to minPx. Mirrors the PDF's drawFitted so the preview shows
// the same shrink behaviour (not a truncation) for long headings.
let _measureCanvas: HTMLCanvasElement | null = null
function fitHeadingPx(text: string, maxW: number, base: number, minPx: number, letterPx: number): number {
  if (!text) return base
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas')
  const ctx = _measureCanvas.getContext('2d')
  if (!ctx) return base
  const t = text.toUpperCase()
  const width = (s: number) => {
    ctx.font = `800 ${s}px Arial, Helvetica, sans-serif`
    return ctx.measureText(t).width + Math.max(0, t.length - 1) * letterPx
  }
  let s = base
  while (s > minPx && width(s) > maxW) s -= 0.5
  return s
}

function measurePx(text: string, fontCss: string): number {
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas')
  const ctx = _measureCanvas.getContext('2d')
  if (!ctx) return 0
  ctx.font = fontCss
  return ctx.measureText(text).width
}

function subFitsOneLine(text: string, maxW: number, sizePx: number, letterPx: number): boolean {
  const t = text.trim()
  if (!t) return true
  if (!_measureCanvas) _measureCanvas = document.createElement('canvas')
  const ctx = _measureCanvas.getContext('2d')
  if (!ctx) return true
  ctx.font = `700 ${sizePx}px Arial, Helvetica, sans-serif`
  const w = ctx.measureText(t.toUpperCase()).width + Math.max(0, t.length - 1) * letterPx
  return w <= maxW
}

// Live approximation of the printed 88x55mm tag. Not pixel-perfect to the
// PDF (browser fonts differ from the embedded TT Chocolates), but it tracks
// the real layout, field sizes, 2-line subheading wrap, and heading
// shrink-to-fit so ops can see the result before printing.
function PreviewTag({ heading, subheading, code, priceJd }: {
  heading: string; subheading: string; code: string; priceJd: string
}) {
  const S = 0.8
  const W = Math.round(249.45 * S)   // ~200
  const H = Math.round(155.9 * S)    // ~125
  const px = (pt: number) => pt * S
  const v = (parseFloat(priceJd) || 0).toFixed(2)
  const [intPart, fracDigits] = v.split('.')
  const frac = `.${fracDigits}`
  // Mirror the PDF's price block: decimals sit at a small gap after the
  // big number; JOD (bold) is centered over the fraction's digits.
  const fracFontPx = px(31)
  const jodFontPx = px(14)
  const bFrac = `800 ${fracFontPx}px Arial, Helvetica, sans-serif`
  const bJod = `800 ${jodFontPx}px Arial, Helvetica, sans-serif`
  const dotW = measurePx('.', bFrac)
  const digitsW = measurePx(fracDigits, bFrac)
  const jodW = measurePx('JOD', bJod)
  const jodLeft = dotW + digitsW / 2 - jodW / 2
  const headMax = W - px(16) - px(10)
  const headSize = fitHeadingPx(heading, headMax, px(16), px(9), 0.4)
  // Does the subheading fit one line? Drives the price height like the PDF
  // (short subheading -> price higher -> bigger gap above the wordmark).
  const subOneLine = subFitsOneLine(subheading, headMax, px(12), 0.3)
  const priceBottom = subOneLine ? px(43) : px(40)

  return (
    <div
      style={{
        width: W, height: H, position: 'relative', borderRadius: 6, overflow: 'hidden',
        backgroundImage: "url('/price-tag-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center',
        fontFamily: 'Arial, Helvetica, sans-serif', color: '#fff',
      }}
    >
      <div style={{
        position: 'absolute', left: px(16), top: px(6), right: px(10),
        fontSize: headSize, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{heading || ' '}</div>

      {subheading.trim() && (
        <div style={{
          position: 'absolute', left: px(16), top: px(22), right: px(10),
          fontSize: px(12), fontWeight: 700, letterSpacing: 0.3, lineHeight: 1.1, textTransform: 'uppercase',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>{subheading}</div>
      )}

      <div style={{ position: 'absolute', left: px(16), bottom: priceBottom, display: 'flex', alignItems: 'baseline', lineHeight: 1 }}>
        <span style={{ fontSize: px(81), fontWeight: 800, lineHeight: 1 }}>{intPart}</span>
        <span style={{ position: 'relative', marginLeft: px(4), fontSize: fracFontPx, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>
          {frac}
          <span style={{ position: 'absolute', left: jodLeft, bottom: fracFontPx * 0.85, fontSize: jodFontPx, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap' }}>JOD</span>
        </span>
      </div>

      {code.trim() && (
        <div style={{ position: 'absolute', right: px(10), bottom: px(8), fontSize: px(7.5), letterSpacing: 1, fontWeight: 600 }}>
          CODE:{code}
        </div>
      )}
    </div>
  )
}
